var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { getAutomation, getDebug } from '@citrus/runtime';
import { insertAt, isNotNullOrEmpty, isNullOrEmpty, removeAt } from '@pcty/utils';
import { css as emotionCss, cx } from 'emotion';
import { cloneDeep, findIndex, isEqual, map } from 'lodash';
import React from 'react';
import { default as ReactSortable } from 'react-sortablejs';
import { drag, ghost, sortable } from '../styles';
import { drag as dragCitrus2022, ghost as ghostCitrus2022, sortable as sortableCitrus2022 } from '../styles/citrus2022';
import getItemIndexMap from '../utils/getItemIndexMap';
import { Styled } from './Sortable.styled';
import Item from './_Item';
import Section from './_Section';
import { withTheme } from './private/withTheme';
import Row from './Row';
import Column from './Column';
var VERSION = '2.5.0';
var Sortable = /** @class */ (function (_super) {
    __extends(Sortable, _super);
    function Sortable(props) {
        var _this = this;
        var _a;
        _this = _super.call(this, props) || this;
        _this.getItemElementIds = _this.getItemElementIds.bind(_this);
        _this.getSectionElementIds = _this.getSectionElementIds.bind(_this);
        _this.onDrop = _this.onDrop.bind(_this);
        _this.onMove = _this.onMove.bind(_this);
        _this.blockChange = false;
        var itemIndexMap = getItemIndexMap(props.data);
        _this.state = {
            data: props.data,
            disabled: (_a = props.disabled) !== null && _a !== void 0 ? _a : false,
            itemIndexMap: itemIndexMap,
        };
        _this.sortableOptions = {
            group: {
                name: 'pcty-sortable-section-' + props.id,
            },
            draggable: '.pcty-sortable-draggable',
            handle: '.pcty-sortable-handle',
            ghostClass: emotionCss(props.theme.legacy ? ghost : ghostCitrus2022),
            dragClass: emotionCss(props.theme.legacy ? drag : dragCitrus2022),
            animation: 250,
            forceFallback: true,
            onMove: _this.onMove,
        };
        return _this;
    }
    Sortable.getDerivedStateFromProps = function (props, state) {
        var newState = {};
        if (!isEqual(state.data, props.data)) {
            var itemIndexMap = getItemIndexMap(props.data);
            newState = __assign(__assign({}, newState), { itemIndexMap: itemIndexMap, data: props.data });
        }
        if (state.disabled !== props.disabled) {
            newState = __assign(__assign({}, newState), { disabled: props.disabled });
        }
        return newState;
    };
    Sortable.prototype.onDrop = function (order, sortable, evt) {
        var _this = this;
        var targetId = evt.to.id
            .replace(/-empty-target-top$/, '')
            .replace(/-empty-target-middle$/, '')
            .replace(/-empty-target-bottom$/, '');
        var itemId = getDroppedItemId(this.props.id, evt);
        var sectionIndexes = getSectionIndexes(this.props.id, this.state.data, targetId, itemId);
        var newIndex = getNewIndex(sectionIndexes.new >= 0, !!this.props.renderHeader, evt, this.state.data);
        var itemIndexes = getItemIndexes(newIndex, sectionIndexes, this.state.data, itemId);
        var item = getDroppedItem(sectionIndexes, itemIndexes, this.state.data);
        var data = cloneDeep(this.state.data);
        if (sectionIndexes.old >= 0) {
            data[sectionIndexes.old] = __assign(__assign({}, data[sectionIndexes.old]), { items: removeAt(data[sectionIndexes.old].items, itemIndexes.old) });
        }
        else {
            data = removeAt(data, itemIndexes.old);
        }
        // If the item was not in a section and was moved into a section below it, adjust the new section index
        // because the item has been removed from the array and the new target section has a new index.
        if (sectionIndexes.old < 0 && sectionIndexes.new >= 0 && itemIndexes.old < sectionIndexes.new) {
            sectionIndexes.new = sectionIndexes.new - 1;
        }
        if (sectionIndexes.new >= 0) {
            // Moving into or within a section.
            if (itemIndexes.new === 0) {
                data[sectionIndexes.new].items = __spreadArray([item], data[sectionIndexes.new].items, true);
            }
            else if (itemIndexes.new >= data[sectionIndexes.new].items.length) {
                data[sectionIndexes.new].items = __spreadArray(__spreadArray([], data[sectionIndexes.new].items, true), [item], false);
            }
            else {
                data[sectionIndexes.new].items = insertAt(data[sectionIndexes.new].items, itemIndexes.new, [item]);
            }
            // Moving out of or outside of a section.
        }
        else if (itemIndexes.new === 0) {
            data = __spreadArray([item], data, true);
        }
        else if (itemIndexes.new >= data.length) {
            data = __spreadArray(__spreadArray([], data, true), [item], false);
        }
        else {
            data = insertAt(data, itemIndexes.new, [item]);
        }
        if (this.blockChange) {
            this.blockChange = false;
            return;
        }
        var itemIndexMap = getItemIndexMap(data);
        this.setState({ data: data, itemIndexMap: itemIndexMap }, function () { return _this.props.onChange(data, item); });
        this.blockChange = sectionIndexes.old !== sectionIndexes.new;
    };
    Sortable.prototype.onMove = function (evt) {
        if (evt.dragged.classList.contains('pcty-sortable-section') && evt.to.classList.contains('pcty-sortable-section')) {
            return false;
        }
        if (evt.dragged.classList.contains('pcty-sortable-section') &&
            evt.to.classList.contains('pcty-sortable-section-empty')) {
            return false;
        }
        if (evt.dragged.parentNode.classList.contains('pcty-sortable-section-empty') &&
            evt.to.classList.contains('pcty-sortable-section')) {
            return false;
        }
        if ((this.props.itemsMustBeInSection &&
            evt.dragged.classList.contains('pcty-sortable-item') &&
            !evt.to.classList.contains('pcty-sortable-section')) ||
            evt.to.classList.contains('pcty-sortable-section-empty-target-middle')) {
            return false;
        }
        if (this.props.itemsMustStayInSection &&
            evt.dragged.classList.contains('pcty-sortable-item') &&
            evt.dragged.getAttribute('data-section-id') !== evt.to.getAttribute('data-section-id')) {
            return false;
        }
        return true;
    };
    Sortable.prototype.getItemElementIds = function () {
        var _this = this;
        return this.state.data.reduce(function (ids, section) { return __spreadArray(__spreadArray([], ids, true), section.items.map(function (x) { return "".concat(_this.props.id, "-item-").concat(x.id); }), true); }, []);
    };
    Sortable.prototype.getSectionElementIds = function () {
        var _this = this;
        return this.state.data.map(function (x) { return "".concat(_this.props.id, "-section-").concat(x.id); });
    };
    Sortable.prototype.render = function () {
        var _a;
        var _this = this;
        var emotionObj = (_a = {},
            _a[emotionCss(sortable)] = this.props.theme.legacy,
            _a[emotionCss(sortableCitrus2022)] = this.props.theme.citrus2022,
            _a[emotionCss(this.props.css)] = isNotNullOrEmpty(this.props.css),
            _a);
        var classes = cx(emotionObj, this.props.className || '');
        if (this.props.disabled) {
            return (React.createElement("div", __assign({}, getAutomation(this.props.automationId), getDebug(sessionStorage.getItem('citrus-debug') === 'true', this.props.__TYPE, VERSION), { className: "".concat(classes, " pcty-sortable-disabled"), id: this.props.id }),
                this.renderHeader(),
                map(this.state.data, function (x, index) { return _this.renderSortable(x, index); })));
        }
        return (React.createElement(ReactSortable, __assign({}, getAutomation(this.props.automationId), getDebug(sessionStorage.getItem('citrus-debug') === 'true', this.props.__TYPE, VERSION), { className: classes, id: this.props.id, onChange: this.onDrop, options: this.sortableOptions }),
            this.renderHeader(),
            this.renderEmptySectionTop(this.state.data),
            map(this.state.data, function (x, index) { return _this.renderSortable(x, index); }),
            this.renderEmptySectionBottom(this.state.data)));
    };
    Sortable.prototype.renderHeader = function () {
        if (!this.props.renderHeader)
            return null;
        return (React.createElement(Styled.Header, null,
            React.createElement(Styled.Column, null),
            this.props.renderHeader()));
    };
    Sortable.prototype.renderSubHeader = function () {
        if (!this.props.renderSubHeader)
            return null;
        return (React.createElement(Styled.Header, null,
            React.createElement(Styled.Column, null),
            this.props.renderSubHeader()));
    };
    Sortable.prototype.renderSortable = function (item, index) {
        if ('items' in item) {
            return this.renderSection(item);
        }
        return this.renderItem(item, { index: index });
    };
    Sortable.prototype.renderSection = function (section) {
        var _this = this;
        return (React.createElement(Section, { disabled: this.props.disabled || section._disabled, id: "".concat(this.props.id, "-section-").concat(section.id), key: "".concat(this.props.id, "-section-").concat(section.id), onDrop: this.onDrop, rawId: section.id, renderSection: this.props.renderSection, renderSubheader: (section === null || section === void 0 ? void 0 : section.renderSubHeader) || null, sortableOptions: this.sortableOptions, subtitle: section.subtitle, title: section.title },
            this.renderSubHeader && this.renderSubHeader(),
            map(section.items, function (x) { return _this.renderItem(x, { section: section }); })));
    };
    Sortable.prototype.renderItem = function (item, _a) {
        var index = _a.index, section = _a.section;
        return (React.createElement(Item, { disabled: item._disabled || (section && section._disabled), gridLines: this.props.gridLines, id: "".concat(this.props.id, "-item-").concat(item.id), index: !this.props.renderHeader ? index : undefined, key: "".concat(this.props.id, "-item-").concat(item.id), sectionId: section ? section.id : undefined }, !!this.props.renderItem ? this.props.renderItem(item, this.state.itemIndexMap[item.id]) : item));
    };
    Sortable.prototype.renderEmptySectionTop = function (data) {
        if (isNullOrEmpty(data) || !data[0]._disabled || 'items' in data[0]) {
            return (React.createElement(Styled.SectionEmpty, { id: this.props.id + '-empty-target-top', onChange: this.onDrop, options: this.sortableOptions }));
        }
        return null;
    };
    Sortable.prototype.renderEmptySectionBottom = function (data) {
        if (isNullOrEmpty(data) || !data[data.length - 1]._disabled || 'items' in data[data.length - 1]) {
            return (React.createElement(Styled.SectionEmpty, { id: this.props.id + '-empty-target-bottom', onChange: this.onDrop, options: this.sortableOptions }));
        }
        return null;
    };
    Sortable.defaultProps = {
        disabled: false,
        gridLines: true,
        itemsMustBeInSection: false,
        itemsMustStayInSection: false,
        __TYPE: 'Sortable',
    };
    return Sortable;
}(React.Component));
var getSectionIndexForItem = function (data, itemId) {
    for (var i = 0; i < data.length; i = i + 1) {
        if (findIndex(data[i].items, function (x) { return x.id === itemId; }) >= 0) {
            return i;
        }
    }
    return -1;
};
var getSectionIndexByElementId = function (data, propsId, sectionElementId) {
    return findIndex(data, function (x) { return "".concat(propsId, "-section-").concat(x.id) === sectionElementId; });
};
var getNewIndex = function (inSection, hasHeader, evt, data) {
    var emptyTarget = {
        top: isNotNullOrEmpty(evt.to.id.match(/-empty-target-top$/)),
        middle: isNotNullOrEmpty(evt.to.id.match(/-empty-target-middle$/)),
        bottom: isNotNullOrEmpty(evt.to.id.match(/-empty-target-bottom$/)),
    };
    var newIndex = hasHeader || inSection ? evt.newIndex - 1 : evt.newIndex;
    if (emptyTarget.top) {
        newIndex = 0;
    }
    else if (emptyTarget.middle) {
        newIndex = 0;
    }
    else if (emptyTarget.bottom) {
        newIndex = data.length;
    }
    else {
        newIndex = newIndex > 0 && !data[0]._disabled ? newIndex - 1 : newIndex;
    }
    return newIndex;
};
var getDroppedItemId = function (propsId, evt) {
    var itemId = evt.item.id;
    if (itemId.indexOf(propsId + '-section-') === 0) {
        itemId = itemId.replace(propsId + '-section-', '').replace('-wrapper', '');
    }
    else {
        itemId = itemId.replace(propsId + '-item-', '');
    }
    return itemId;
};
var getSectionIndexes = function (propsId, data, targetId, itemId) { return ({
    new: getSectionIndexByElementId(data, propsId, targetId),
    old: getSectionIndexForItem(data, itemId),
}); };
var getItemIndexes = function (newIndex, sectionIndexes, data, itemId) { return ({
    new: newIndex,
    old: sectionIndexes.old >= 0
        ? findIndex(data[sectionIndexes.old].items, function (x) { return x.id === itemId; })
        : findIndex(data, function (x) { return x.id === itemId; }),
}); };
var getDroppedItem = function (sectionIndexes, itemIndexes, data) { return (sectionIndexes.old >= 0 ? __assign({}, data[sectionIndexes.old].items[itemIndexes.old]) : __assign({}, data[itemIndexes.old])); };
export default Object.assign(withTheme(Sortable), {
    Row: Row,
    Column: Column,
});
